import { isPrejoinPageVisible } from "../../features/base/conference";
import { getDeviceLabelById } from "../../features/base/devices";
import { devicePermissionsChanged, notifyCameraError, notifyMicError, updateDeviceList } from "../../features/base/devices/actions";
import JitsiMeetJS, { JitsiMediaDevicesEvents, JitsiTrackErrors, JitsiTrackEvents } from "../../features/base/lib-jitsi-meet";
import { isAudioMuted, isVideoMutedByUser, MEDIA_TYPE, setAudioMuted, setVideoMuted } from "../../features/base/media";
import { LOCAL_PARTICIPANT_DEFAULT_ID } from "../../features/base/participants";
import {
    getUserSelectedCameraDeviceId,
    getUserSelectedMicDeviceId,
    updateSettings
} from "../../features/base/settings";
import {
    getLocalAudioTrack,
    getLocalTracks,
    getLocalVideoTrack,
    isLocalCameraTrackMuted,
    isLocalTrackMuted,
    isUserInteractionRequiredForUnmute,
    noDataFromSource,
    replaceLocalTrack,
    trackAdded,
    trackMutedChanged,
    trackStopped
} from "../../features/base/tracks";
import loadEffects from "../../features/base/tracks/loadEffects";
import { setDeviceStatusOk } from "../../features/device/actions";
import { isScreenAudioShared } from "../../features/screen-share";
import { createTaskQueue } from "../../modules/util/helpers";

const _replaceLocalVideoTrackQueue = createTaskQueue();
const _replaceLocalAudioTrackQueue = createTaskQueue();

const JITSI_TRACK_ERROR_TO_MESSAGE_KEY_MAP = {
    microphone: {
        [JitsiTrackErrors.CONSTRAINT_FAILED]: 'dialog.micConstraintFailedError',
        [JitsiTrackErrors.GENERAL]: 'dialog.micUnknownError',
        [JitsiTrackErrors.NOT_FOUND]: 'dialog.micNotFoundError',
        [JitsiTrackErrors.PERMISSION_DENIED]: 'dialog.micPermissionDeniedError'
    },
    camera: {
        [JitsiTrackErrors.CONSTRAINT_FAILED]: 'dialog.cameraConstraintFailedError',
        [JitsiTrackErrors.GENERAL]: 'dialog.cameraUnknownError',
        [JitsiTrackErrors.NOT_FOUND]: 'dialog.cameraNotFoundError',
        [JitsiTrackErrors.PERMISSION_DENIED]: 'dialog.cameraPermissionDeniedError',
        [JitsiTrackErrors.UNSUPPORTED_RESOLUTION]: 'dialog.cameraUnsupportedResolutionError'
    }
};


let _initDeviceStatus = false;

class Device {
    constructor() {
        this.handler;
        this.localVideo = null;
        this.localPresenterVideo = null;

        this._untoggleScreenSharing = null;

        this.handlerUpdateAudioLevel = this.updateAudioLevel.bind(this);
    }

    init(handler) {
        this.handler = handler;

        this.checkDevicePermission();
    }

    // init Device List 
    _initDeviceList(setDeviceListChangeHandler = false) {
        const { mediaDevices } = JitsiMeetJS;

        if (mediaDevices.isDeviceListAvailable() && mediaDevices.isDeviceChangeAvailable()) {
            if (setDeviceListChangeHandler) {    
                // device list 변경 EventListener
                // this.deviceChangeListener = devices => {
                //     window.setTimeout(() => this._onDeviceListChanged(devices), 0);
                // };                
                // mediaDevices.addEventListener(
                //     JitsiMediaDevicesEvents.DEVICE_LIST_CHANGED, this.deviceChangeListener);
            }

            // 가능한 장치 list 가져오기 
            return this.getAvailableDevices();
        }

        return Promise.resolve();
    }

    // 장치 설정 init
    setInitDevice() {
        if (!_initDeviceStatus) {
            return new Promise( async (resolve, reject) => {
                // 장치 권한 확인
                this.checkDevicePermission();

                // get Device List 
                const initialOptions = {
                    startAudioOnly: false,
                    startWithAudioMuted: false,
                    startWithVideoMuted: false,
                    startScreenSharing: false
                };
                
                this._initDeviceList(true)
                    .then(() => {
                        const { tryCreateLocalTracks, errors } = this.createInitialLocalTracks(initialOptions);

                        tryCreateLocalTracks
                            .then((tracks) => {
                                this._displayErrorsForCreateInitialLocalTracks(errors);
                                this._setLocalAudioVideoStreams(tracks);

                                setTimeout(() => resolve(true), 1000);
                            });

                        _initDeviceStatus = true;
                        
                    })
                    .catch((error) => {
                        console.log("device list initialization  failed", error);
                    });                
                
            });
        }

        return Promise.resolve(false);
    }
    
    // audio, video 사용
    _setLocalAudioVideoStreams(tracks = []) {
        const { dispatch, getState } = APP.store;

        return tracks.map(track => {
            if (track.isAudioTrack()) {
                dispatch(updateSettings({
                    userSelectedMicDeviceId: track.deviceId,
                    userSelectedMicDeviceLabel: getDeviceLabelById(getState(), track.deviceId, 'audioInput')
                }));
    
                return this.useAudioStream(track);
            }
            else if (track.isVideoTrack()) {
                dispatch(updateSettings({
                    userSelectedCameraDeviceId: track.deviceId,
                    userSelectedCameraDeviceLabel:
                        getDeviceLabelById(getState(), track.deviceId, 'videoInput')
                }));

                return this.useVideoStream(track);
            }
            
            console.error('Ignored not an audio nor a video track: ', track);
            return Promise.resolve();
        });
    }

    deviceUnload() {
        _initDeviceStatus = false;
    }

    unload() {
        this.connection && this.connection.disconnect();

        JitsiMeetJS.mediaDevices.removeEventListener(
            JitsiMediaDevicesEvents.DEVICE_LIST_CHANGED,
            this.deviceChangeListener);
    }

    /**
     * audio, video 권한 확인
     */
    checkDevicePermission() {
        const _permissionsListener = permissions => {
            APP.store.dispatch(devicePermissionsChanged(permissions));
        };

        const { mediaDevices } = JitsiMeetJS;
        const permissionsListener = _permissionsListener;

        mediaDevices.addEventListener(
            JitsiMediaDevicesEvents.PERMISSIONS_CHANGED, permissionsListener
        );

        Promise.all([
            mediaDevices.isDevicePermissionGranted('audio'),
            mediaDevices.isDevicePermissionGranted('video')
        ]).then(results => {
            _permissionsListener({
                audio: results[0],
                video: results[1]
            });
        });
    }  

    // 가능한 장치 list 가져오기 
    getAvailableDevices() {
        return new Promise((resolve) => {
            const { mediaDevices } = JitsiMeetJS;
        
            if (mediaDevices.isDeviceListAvailable() && mediaDevices.isDeviceChangeAvailable()) {
                mediaDevices.enumerateDevices(devices => {
                    APP.store.dispatch(updateDeviceList(devices));
                    resolve(devices);
                });
            } else {
                resolve([]);
            }
        });
    }
    /**
     * disableInitialGUM => 사용자가 화면 공유만 하는 경우에 사용 
     * local track 초기 생성  
     * */ 
    createInitialLocalTracks(options) {
        const errors = {};
        const initialDevices = [ 'audio' ];
        const requestedAudio = true;
        let requestedVideo = false;

        if (options.startWithAudioMuted) this.muteAudio(true, true);
        if (!options.startWithVideoMuted && !options.startAudioOnly && !options.startScreenSharing) {
            initialDevices.push('video');
            requestedVideo = true;
        }

        let tryCreateLocalTracks;

        if (!requestedAudio && !requestedVideo) {
            tryCreateLocalTracks = Promise.resolve([]);
        } else {
            tryCreateLocalTracks = this.createLocalTracksF({ devices: initialDevices }, true)
                .catch(err => {
                    if (requestedAudio && requestedVideo) {
                        errors.audioAndVideoError = err;

                        return (
                            this.createLocalTracksF({ devices: [ 'audio' ] }, true));
                    } else if (requestedAudio && !requestedVideo) {
                        errors.audioOnlyError = err;

                        return [];
                    } else if (requestedVideo && !requestedAudio) {
                        errors.videoOnlyError = err;

                        return [];
                    }
                    console.error('Should never happen');
                })
                .catch(err => {
                    if (!requestedAudio) {
                        console.error('The impossible just happened', err);
                    }
                    errors.audioOnlyError = err;

                    return requestedVideo
                        ? this.createLocalTracksF({ devices: [ 'video' ] }, true)
                        : [];
                })
                .catch(err => {
                    if (!requestedVideo) {
                        console.error('The impossible just happened', err);
                    }
                    errors.videoOnlyError = err;

                    return [];
                });
        }

        return {
            tryCreateLocalTracks,
            errors
        };
    }
    /**
     * 특정 유형의 로컬 트랙을 만듭니다.
     */
    createLocalTracksF(options = {}) {
        const state = APP.store.getState();

        let { cameraDeviceId, micDeviceId, devices } = options;
        
        cameraDeviceId = (cameraDeviceId === undefined) ? getUserSelectedCameraDeviceId(state) : options.cameraDeviceId;
        micDeviceId = (micDeviceId === undefined) ? getUserSelectedMicDeviceId(state) : options.micDeviceId;
           
        return (
            loadEffects(APP.store)
                .then(effectsArray => {
                    const effects = effectsArray.filter(effect => Boolean(effect));
                
                    return JitsiMeetJS.createLocalTracks({
                        devices,
                        cameraDeviceId,
                        micDeviceId,
                        effects,
                        facingMode: options.facingMode,
                        desktopSharingFrameRate: 30,
                        constraints: {
                            video: {
                                height: { ideal: 720 },
                                frameRate: { ideal: 30 }
                            }
                        },
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }
            ));
    } 

    // audio stream 사용 
    useAudioStream(newTrack) {
        const { getState, dispatch } = APP.store;

        return new Promise((resolve, reject) => {
            _replaceLocalAudioTrackQueue.enqueue(onFinish => {
                const state = getState();   
                const oldTrack = getLocalAudioTrack(state['features/base/tracks'])?.track;
                
                if (oldTrack === newTrack) {
                    resolve();
                    onFinish();
                }

                const conference = this.handler.getRoom();
                dispatch(replaceLocalTrack(oldTrack, newTrack, conference))
                    .then(resolve)
                    .catch(reject)
                    .then(onFinish)
            });
        });
    }
    // video stream 사용 
    useVideoStream(newTrack) {
        const { getState, dispatch } = APP.store;
        
        return new Promise((resolve, reject) => {
            _replaceLocalVideoTrackQueue.enqueue(onFinish => {
                const state = getState();       
                const oldTrack = getLocalVideoTrack(state['features/base/tracks'])?.track;

                if (oldTrack === newTrack) {
                    resolve();
                    onFinish();
                    return;
                }

                const conference = this.handler.getRoom();
                dispatch(replaceLocalTrack(oldTrack, newTrack, conference))
                    .then(resolve)
                    .catch(reject)
                    .then(onFinish)
            });
        });
    }

    /**
     * TRACK
     */
    // TRACK 저장 
    trackAdded(track) {
        track.on(JitsiTrackEvents.TRACK_MUTE_CHANGED,
            () => APP.store.dispatch(trackMutedChanged(track)));

        // track.on(JitsiTrackEvents.TRACK_VIDEOTYPE_CHANGED,
        //     type =>  APP.store.dispatch(trackVideoTypeChanged(track, type)));

        const local = track.isLocal();
        const mediaType = track.getType();
        let participantId;
        let trackId;
        
        if (local) { 
            trackId = LOCAL_PARTICIPANT_DEFAULT_ID;
            participantId = LOCAL_PARTICIPANT_DEFAULT_ID;  
            track.on(JitsiTrackEvents.NO_DATA_FROM_SOURCE, () => APP.store.dispatch(noDataFromSource({ track })));

            track.on(JitsiTrackEvents.LOCAL_TRACK_STOPPED,
                () => APP.store.dispatch(trackStopped(track)));
        } else {
            trackId = track.getParticipantId();
        }

        APP.store.dispatch(trackAdded({
            track,
            participantId: trackId,
            muted: track.isMuted(),
            local,
            mediaType,
            videoStarted: false,
            videoType: track.type
        }));
    }
    // TRACK 파괴
    disposeTracks(tracks) {
        return Promise.all(
            tracks && tracks.map(t => {
                t && t.dispose && t.dispose()
                    .catch(err => {
                        console.log(err)
                        if (err.name !== JitsiTrackErrors.TRACK_IS_DISPOSED) {
                            throw err;
                        }
                    })
            }
        ));
    } 

    // audio input id 업데이트 
    _updateAudioDeviceId() {
        const localAudio = getLocalAudioTrack(APP.store.getState()['features/base/tracks'])?.track;

        if (localAudio) {
            APP.store.dispatch(updateSettings({
                micDeviceId: localAudio.getDeviceId()
            }));
        }
    }
    // video input id 업데이트
    _updateVideoDeviceId() {
        const localVideo = getLocalVideoTrack(APP.store.getState()['features/base/tracks'])?.track;
        
        if (localVideo && localVideo.videoType === 'camera') {
            APP.store.dispatch(updateSettings({
                cameraDeviceId: localVideo.getDeviceId()
            }));
        }

        if (this.localPresenterVideo) {
            APP.store.dispatch(updateSettings({
                cameraDeviceId: this.localPresenterVideo.getDeviceId()
            }));
        }
    }

    
    _displayErrorsForCreateInitialLocalTracks(errors) {
        const { dispatch } = APP.store;
        const {
            audioAndVideoError,
            audioOnlyError,
            screenSharingError,
            videoOnlyError
        } = errors;
        
        if (screenSharingError) {
            this._handleScreenSharingError(screenSharingError);
        }

        if (audioAndVideoError || audioOnlyError) {
            if (audioOnlyError || videoOnlyError) {
                setNotifyMicError(audioOnlyError);
                setNotifyCameraError(videoOnlyError);
            } else {
                setNotifyCameraError(audioAndVideoError);
            }
        }

        if (!audioOnlyError) dispatch(setDeviceStatusOk('audioInput', {titleKey: 'device.microphoneOk'}));
        if (!videoOnlyError) dispatch(setDeviceStatusOk('videoInput', {titleKey: 'device.cameraOk'}));    
    }

    // 장치 list 변경 
    // async _onDeviceListChanged(devices) {
    //     if (!_initDeviceStatus) return;
    //     const oldDevices = APP.store.getState()['features/base/devices'].availableDevices;

    //     const localAudio = getLocalAudioTrack(APP.store.getState()['features/base/tracks'])?.track;
    //     const localVideo = getLocalVideoTrack(APP.store.getState()['features/base/tracks'])?.track;
    //     APP.store.dispatch(updateDeviceList(devices));

    //     const newLabelsOnly = mediaDeviceHelper.newDeviceListAddedLabelsOnly(oldDevices, devices);
    //     const newDevices = mediaDeviceHelper.getNewMediaDevicesAfterDeviceListChanged(
    //             devices,
    //             this.isSharingScreen,
    //             localVideo,
    //             localAudio,
    //             newLabelsOnly);

    //     const promises = [];
    //     const audioWasMuted = this.isLocalAudioMuted();
    //     const videoWasMuted = this.isLocalVideoMuted();
    //     const requestedInput = {
    //         audio: Boolean(newDevices.audioinput),
    //         video: Boolean(newDevices.videoinput)
    //     };

    //     if (typeof newDevices.audiooutput !== 'undefined') {
    //         const { dispatch } = APP.store;
    //         const setAudioOutputPromise = setAudioOutputDeviceId(newDevices.audiooutput, dispatch).catch();

    //         promises.push(setAudioOutputPromise);
    //     }

    //     if (requestedInput.audio && localAudio) {
    //         localAudio.stopStream();
    //     }

    //     if (requestedInput.video && localVideo) {
    //         localVideo.stopStream();
    //     }

    //     const newAvailDevices = APP.store.getState()['features/base/devices'].availableDevices;
    //     let newAudioDevices = [];
    //     let oldAudioDevices = [];

    //     if (typeof newDevices.audiooutput === 'undefined') {
    //         newAudioDevices = newAvailDevices.audioOutput;
    //         oldAudioDevices = oldDevices.audioOutput;
    //     }

    //     if (!requestedInput.audio) {
    //         newAudioDevices = newAudioDevices.concat(newAvailDevices.audioInput);
    //         oldAudioDevices = oldAudioDevices.concat(oldDevices.audioInput);
    //     }

    //     // check for audio
    //     if (newAudioDevices.length > 0) {
    //         APP.store.dispatch(checkAndNotifyForNewDevice(newAudioDevices, oldAudioDevices));
    //     }        

    //     // check for video
    //     if (!requestedInput.video) {
    //         APP.store.dispatch(checkAndNotifyForNewDevice(newAvailDevices.videoInput, oldDevices.videoInput));
    //     }

    //     const hasDefaultMicChanged = newDevices.audioinput === 'default';
    
    //     if (requestedInput.video && this.isLocalVideoMuted()) {
    //         requestedInput.video = false;
        
    //         APP.store.dispatch(updateSettings({
    //             cameraDeviceId: newDevices.videoinput
    //         }));
    //         delete newDevices.videoinput;

    //         console.log('_onDeviceListChanged: Removing the current video track.');
    //         this.useVideoStream(null);
    //     }

    //     const handler = {
    //         setCameraError: setNotifyCameraError.bind(this),
    //         setAudioError: setNotifyMicError.bind(this),
    //         setCameraOk: () => APP.store.dispatch(setDeviceStatusOk('videoInput', {titleKey: 'device.cameraOk'})),
    //         setAudioOk: () => APP.store.dispatch(setDeviceStatusOk('audioInput', {titleKey: 'device.microphoneOk'})),
    //     };

    //     promises.push(
    //         mediaDeviceHelper.createLocalTracksAfterDeviceListChanged(
    //             this.createLocalTracksF,
    //             newDevices.videoinput,
    //             hasDefaultMicChanged
    //                 ? getDefaultDeviceId(APP.store.getState(), 'audioInput')
    //                 : newDevices.audioinput,
    //             handler)
    //             .then(tracks => {
    //                 const muteSyncPromises = tracks.map(track => {
    //                     if ((track.isVideoTrack() && videoWasMuted)
    //                         || (track.isAudioTrack() && audioWasMuted)) {
    //                         return track.mute();
    //                     }

    //                     return Promise.resolve();
    //                 });

    //                 return Promise.all(muteSyncPromises)
    //                     .then(() => Promise.all(Object.keys(requestedInput).map(mediaType => {
    //                         if (requestedInput[mediaType]) {
    //                             const useStream
    //                                 = mediaType === 'audio'
    //                                     ? this.useAudioStream.bind(this)
    //                                     : this.useVideoStream.bind(this);

    //                             const track = tracks.find(t => t.getType() === mediaType) || null;

    //                             return useStream(track)
    //                                 .then(() => {
    //                                     if (track?.isAudioTrack() && hasDefaultMicChanged) {
    //                                         track._realDeviceId = track.deviceId = 'default';
    //                                     }
    //                                 });
    //                         }

    //                         return Promise.resolve();
    //                     })));
    //             })
    //         .then(() => {
    //             // Log and sync known mute state.
    //             if (audioWasMuted) {
    //                 console.log('Audio mute: device list changed');
    //                 muteLocalAudio(true);
    //             }

    //             if (!this.isSharingScreen && videoWasMuted) {
    //                 console.log('Video mute: device list changed');
    //                 muteLocalVideo(true);
    //             }
    //         }));

    //     // success
    //     return Promise.all(promises).catch(err => {
    //             console.log(err)
    //         });
    // }

    /**
     * TRACK 음소거
     */
    setTrackMuted(track, muted) {
        if (isPrejoinPageVisible(APP.store.getState())) return;
        return track.track[muted]()
            .then(() => {
                
            }).catch(error => {
                setNotifyCameraError(error);
                if (error.name !== JitsiTrackErrors.TRACK_IS_DISPOSED) {
                    console.error(`set track ${muted} failed`, error);
                }
            });
    }
    // 오디오 muted
    isLocalAudioMuted() {
        return this.handler._localTracksInitialized()
            ? isLocalTrackMuted(
                APP.store.getState()['features/base/tracks'],
                MEDIA_TYPE.AUDIO)
            : isAudioMuted(APP.store);
    }
    // 비디오 muted 
    isLocalVideoMuted() {
        // If the tracks are not ready, read from base/media state
        return this.handler._localTracksInitialized()
            ? isLocalCameraTrackMuted(
                APP.store.getState()['features/base/tracks'])
            : isVideoMutedByUser(APP.store);
    }

    // 오디오만 사용 하는가
    isAudioOnly() {
        return Boolean(APP.store.getState()['features/base/audio-only'].enabled);
    }

    /**
     * 화면 공유
     */
    // 화면 공유 track 생성
    _createDesktopTrack(options = {}) {
        const didHaveVideo = !this.isLocalVideoMuted();

        const getDesktopStreamPromise = options.desktopStream
            ? Promise.resolve([ options.desktopStream ])
            : this.createLocalTracksF({
                desktopSharingSourceDevice: null,
                desktopSharingSources: null,
                devices: [ 'desktop' ],
                resolution: 720,
                minFps: 29 
            });

        return getDesktopStreamPromise.then(desktopStreams => {
            this._untoggleScreenSharing = options.unToggleScreenSharing.bind(this, didHaveVideo);

            const desktopVideoStream = desktopStreams.find(stream => stream.getType() === MEDIA_TYPE.VIDEO);
            const dekstopAudioStream = desktopStreams.find(stream => stream.getType() === MEDIA_TYPE.AUDIO);

            if (dekstopAudioStream) {
                dekstopAudioStream.on(JitsiTrackEvents.LOCAL_TRACK_STOPPED, () => {
                    isScreenAudioShared(APP.store.getState()) && this._untoggleScreenSharing 
                    && this._untoggleScreenSharing();
                });
            }
            
            if (desktopVideoStream) {
                desktopVideoStream.on(JitsiTrackEvents.LOCAL_TRACK_STOPPED, () => { 
                    this._untoggleScreenSharing && this._untoggleScreenSharing();
                });
            }

            return desktopStreams;
        }, (error) => {
            throw error;
        });
    }

    /**
     * conference 
     */
    // conference track 저장 
    addLocalTracksToConference(localTracks) {
        const room = this.handler.getRoom();
        
        if (room) {
            localTracks = localTracks 
                ? localTracks 
                : getLocalTracks(APP.store.getState()['features/base/tracks']).map(t => t.track);
                    
            const promises = [];  

            for (const track of localTracks) {                
                if (track) {                
                    promises.push(
                        room.addTrack(track)
                            .catch(err => {
                                console.error(
                                    'Failed to add local track to conference',
                                    err);
                            }));
                } 
            }

            return Promise.all(promises);
        } else {
            return false;
        }      
    }
    // conference track 재배치
    async replaceConferenceTrack(oldTrack, newTrack) {
        const conference = this.handler.getRoom();
        if (conference) {
            return await conference.replaceTrack(oldTrack, newTrack);
        }
    }
    // conference track 삭제 
    removeLocalTracksFromConference(localTracks) {
        if (this.handler.getRoom()) {
            return Promise.all(localTracks.map(track =>
                this.handler.getRoom().removeTrack(track)
                    .catch(err => {
                        if (err.name !== JitsiTrackErrors.TRACK_IS_DISPOSED) {
                            console.error(
                                'Failed to remove local track from conference',
                                err);
                        }
                    })
            ));
        }
    }

    /**
     * Video Input
     */
    toggleVideoMuted(showUI) {
        this.muteVideo(!this.isLocalVideoMuted(), showUI);
    }

    toggleAudioMuted(showUI) {
        this.muteAudio(!this.isLocalAudioMuted(), showUI);
    }

    muteVideo(mute, showUI = true) {
        if (!mute && isUserInteractionRequiredForUnmute(APP.store.getState())) {
            console.error('Unmuting video requires user interaction');

            return;
        }

        if (!this._localTracksInitialized) {
            muteLocalVideo(mute);
            return;
        } else if (this.isLocalVideoMuted() === mute) return;
    }

    muteAudio(mute, showUI = true) {
        if (!mute && isUserInteractionRequiredForUnmute(APP.store.getState())) {
            console.log('Unmuting audio requires user interaction');

            return;
        }
        if (!this._localTracksInitialized) {
            muteLocalAudio(mute);
            return;
        } else if (this.isLocalVideoMuted() === mute) return;
    }
    
    /**
     * Audio Input 
     */
    // audio level event listener 저장 
    listenForAudioUpdates({ track }) {
        this._stopListenForAudioUpdates();

        track && track.on(
            JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, 
            this.handlerUpdateAudioLevel
        );
    }
    // audio level event listener 삭제
    _stopListenForAudioUpdates(track) {
        const localAudio = track ? track.track : getLocalAudioTrack(APP.store.getState()['features/base/tracks'])?.track;
        
        localAudio && localAudio.off(
            JitsiTrackEvents.TRACK_AUDIO_LEVEL_CHANGED, 
            this.handlerUpdateAudioLevel
        );
    }
    // UI
    updateAudioLevel(level) {
        APP.UI.updateAudioLevel(level);
    }

    /**
     * Audio Output
     */
    getAudioOutputDeviceId() {
        return JitsiMeetJS.mediaDevices.getAudioOutputDevice();   
    }
    setAudioOutputDevice(newId) {
        return JitsiMeetJS.mediaDevices.setAudioOutputDevice(newId);        
    }
}

function muteLocalAudio(muted) {
    APP.store.dispatch(setAudioMuted(muted));
}

function muteLocalVideo(muted) {
    APP.store.dispatch(setVideoMuted(muted));
}

// 마이크 오류 표시 
function setNotifyMicError(error) {
   const message = getErrorMicMsg(error);
    
    APP.store.dispatch(notifyMicError(message));
}

// 비디오 오류 표시 
 function setNotifyCameraError(error) {
    const message = getErrorCameraMsg(error);

    APP.store.dispatch(notifyCameraError(message));
}

export function getErrorMicMsg(error) {
    if (!error) return;
    const { message, name } = error;

    const micJitsiTrackErrorMsg
        = JITSI_TRACK_ERROR_TO_MESSAGE_KEY_MAP.microphone[name];
    const micErrorMsg = micJitsiTrackErrorMsg
        || JITSI_TRACK_ERROR_TO_MESSAGE_KEY_MAP
            .microphone[JitsiTrackErrors.GENERAL];
    const additionalMicErrorMsg = micJitsiTrackErrorMsg ? null : message;
    const titleKey = name === JitsiTrackErrors.PERMISSION_DENIED
        ? 'deviceError.microphonePermission'
        : 'deviceError.microphoneError';

    return {
        micErrorMsg,
        additionalMicErrorMsg,
        titleKey
    };
}   

export function getErrorCameraMsg(error) {
    if (!error) return;
    const { message, name } = error;

    const cameraJitsiTrackErrorMsg = JITSI_TRACK_ERROR_TO_MESSAGE_KEY_MAP.camera[name];
    const cameraErrorMsg = cameraJitsiTrackErrorMsg 
        || JITSI_TRACK_ERROR_TO_MESSAGE_KEY_MAP.camera[JitsiTrackErrors.GENERAL];
    const additionalCameraErrorMsg = cameraJitsiTrackErrorMsg ? null : message;
    const titleKey = name === JitsiTrackErrors.PERMISSION_DENIED
        ? 'deviceError.cameraPermission' : 'deviceError.cameraError';

    return {
        cameraErrorMsg,
        additionalCameraErrorMsg,
        titleKey
    };
}

const deviceManage = new Device();
export default deviceManage;